import React, { useCallback, useEffect, useState } from 'react';
import MainLayout from '../../layouts/MainLayout';
import WorkListPage from '../../components/work/WorkListPage';
import {
  Alert,
  Avatar,
  Button,
  Checkbox,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Popconfirm,
  Result,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  Tag,
  Tooltip,
} from 'antd';
import { TeamOutlined, PlusOutlined, ReloadOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { liveChatApi, show422Error, ValidationError, ForbiddenError } from '../../utils/api';
import { getImagePath } from '../../utils/imageHelper';
import { TableSkeleton } from '../../components/SkeletonLoaders';
import { PANEL_PRIMARY_BTN_CLASS } from '../../constants/ui';
import { SourceTag, OPERATOR_STATUS } from '../../components/live-chat/liveChatMeta';

const LiveChatOperators = () => {
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [operators, setOperators] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formMeta, setFormMeta] = useState({ employees: [], sources: [], departments: [] });
  const [loadingMeta, setLoadingMeta] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [form] = Form.useForm();
  const allSources = Form.useWatch('all_sources', form);

  const loadOperators = useCallback(async () => {
    setLoading(true);
    try {
      const res = await liveChatApi.getOperators();
      setOperators(res?.data ?? []);
    } catch (error) {
      if (error instanceof ForbiddenError || error?.status === 403) setForbidden(true);
      else message.error(error?.message || 'خطا در دریافت اپراتورها');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOperators();
  }, [loadOperators]);

  const openModal = async (operator = null) => {
    setEditing(operator);
    setModalOpen(true);
    form.resetFields();
    form.setFieldsValue(
      operator
        ? {
            user_id: operator.user_id,
            display_name: operator.display_name,
            all_sources: operator.all_sources,
            source_ids: (operator.sources || []).map((s) => s.id),
            department_ids: (operator.departments || []).map((d) => d.id),
            max_concurrent_chats: operator.max_concurrent_chats,
            is_active: operator.is_active,
          }
        : { all_sources: false, source_ids: [], department_ids: [], is_active: true },
    );
    setLoadingMeta(true);
    try {
      const res = await liveChatApi.getOperatorForm();
      const deps = await liveChatApi.getDepartments().catch(() => null);
      setFormMeta({
        employees: res?.data?.employees ?? [],
        sources: res?.data?.sources ?? [],
        departments: (deps?.data ?? []).filter((d) => d.is_active),
      });
    } catch (error) {
      message.error(error?.message || 'خطا در دریافت اطلاعات فرم');
      setModalOpen(false);
    } finally {
      setLoadingMeta(false);
    }
  };

  const submit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      const payload = {
        display_name: values.display_name || null,
        all_sources: Boolean(values.all_sources),
        source_ids: values.all_sources ? [] : values.source_ids || [],
        department_ids: values.department_ids || [],
        max_concurrent_chats: values.max_concurrent_chats || null,
        is_active: values.is_active !== false,
      };
      if (editing) {
        await liveChatApi.updateOperator(editing.id, payload);
        message.success('اپراتور بروزرسانی شد');
      } else {
        await liveChatApi.createOperator({ ...payload, user_id: values.user_id });
        message.success('اپراتور ثبت شد');
      }
      setModalOpen(false);
      setEditing(null);
      await loadOperators();
    } catch (error) {
      if (error?.errorFields) return;
      if (error instanceof ValidationError) show422Error(error, form);
      else message.error(error?.message || 'خطا در ذخیره اپراتور');
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (row) => {
    setDeletingId(row.id);
    try {
      await liveChatApi.removeOperator(row.id);
      message.success('اپراتور حذف شد');
      await loadOperators();
    } catch (error) {
      message.error(error?.message || 'خطا در حذف اپراتور');
    } finally {
      setDeletingId(null);
    }
  };

  const toggleActive = async (row, isActive) => {
    try {
      await liveChatApi.updateOperator(row.id, { is_active: isActive });
      setOperators((prev) => prev.map((o) => (o.id === row.id ? { ...o, is_active: isActive } : o)));
    } catch (error) {
      message.error(error?.message || 'خطا در تغییر وضعیت');
    }
  };

  const columns = [
    {
      title: 'اپراتور',
      key: 'user',
      render: (_, row) => (
        <Space>
          <Avatar src={getImagePath(row.user?.image_url || row.user?.image)} size={40}>
            {(row.user?.name || '?').charAt(0)}
          </Avatar>
          <div>
            <div className="custom-font font-medium">{row.display_name || row.user?.name || '—'}</div>
            <div className="custom-font text-xs text-gray-500">
              {row.display_name ? `${row.user?.name} · ` : ''}
              {row.user?.email}
            </div>
          </div>
        </Space>
      ),
    },
    {
      title: 'منابع مجاز',
      key: 'sources',
      render: (_, row) =>
        row.all_sources ? (
          <Tag color="cyan" className="custom-font">همه منابع (شامل منابع آینده)</Tag>
        ) : (
          <Space size={[4, 4]} wrap>
            {(row.sources || []).map((s) => (
              <SourceTag key={s.id} source={s} />
            ))}
          </Space>
        ),
    },
    {
      title: 'بخش‌ها',
      key: 'departments',
      render: (_, row) =>
        row.departments?.length ? (
          <Space size={[4, 4]} wrap>
            {row.departments.map((d) => (
              <Tag key={d.id} color="geekblue" className="custom-font">{d.name}</Tag>
            ))}
          </Space>
        ) : (
          <span className="text-xs text-gray-400 custom-font">همه بخش‌ها</span>
        ),
    },
    {
      title: 'وضعیت حضور',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (status) => (
        <Tag color={OPERATOR_STATUS[status]?.color} className="custom-font">
          {OPERATOR_STATUS[status]?.label || status}
        </Tag>
      ),
    },
    {
      title: 'گفتگوی باز',
      dataIndex: 'open_conversations_count',
      key: 'open',
      width: 100,
      render: (count, row) => (
        <span className="custom-font">
          {Number(count || 0).toLocaleString('fa-IR')}
          {row.max_concurrent_chats ? <span className="text-gray-400"> / {row.max_concurrent_chats.toLocaleString('fa-IR')}</span> : null}
        </span>
      ),
    },
    {
      title: 'فعال',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 90,
      render: (active, row) => <Switch size="small" checked={active} onChange={(v) => toggleActive(row, v)} />,
    },
    {
      title: 'عملیات',
      key: 'actions',
      width: 110,
      render: (_, row) => (
        <Space>
          <Tooltip title="ویرایش">
            <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openModal(row)} />
          </Tooltip>
          <Popconfirm
            title="حذف این اپراتور؟"
            description="گفتگوهای باز او بدون اپراتور می‌شوند."
            okText="بله"
            cancelText="خیر"
            onConfirm={() => remove(row)}
          >
            <Button type="link" danger size="small" icon={<DeleteOutlined />} loading={deletingId === row.id} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  if (forbidden) {
    return (
      <MainLayout>
        <Result status="403" title={<span className="custom-font">دسترسی به مدیریت اپراتورها ندارید</span>} />
      </MainLayout>
    );
  }

  return (
    <WorkListPage
      title="اپراتورهای گفتگوی آنلاین"
      subtitle="تعیین کارمندان پشتیبان و منابعی که هر کدام پاسخ می‌دهند"
      icon={TeamOutlined}
      headerExtra={(
        <>
          <Button icon={<ReloadOutlined />} onClick={loadOperators} loading={loading} style={{ fontFamily: 'CustomFont' }}>
            بروزرسانی
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => openModal()} className={PANEL_PRIMARY_BTN_CLASS} style={{ fontFamily: 'CustomFont' }}>
            افزودن اپراتور
          </Button>
        </>
      )}
    >
      {loading ? (
        <TableSkeleton rows={5} columns={6} />
      ) : (
        <Table
          rowKey="id"
          columns={columns}
          dataSource={operators}
          pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: ['10', '20', '50'] }}
          locale={{ emptyText: 'اپراتوری ثبت نشده است' }}
        />
      )}

      <Modal
        title={editing ? 'ویرایش اپراتور' : 'افزودن اپراتور'}
        open={modalOpen}
        onCancel={() => {
          setModalOpen(false);
          setEditing(null);
        }}
        onOk={submit}
        confirmLoading={submitting}
        destroyOnHidden
        okText={editing ? 'ذخیره' : 'ثبت'}
        cancelText="انصراف"
        width={540}
        okButtonProps={{ className: PANEL_PRIMARY_BTN_CLASS }}
      >
        <Spin spinning={loadingMeta}>
          <Form form={form} layout="vertical" className="custom-font" style={{ marginTop: 8 }}>
            <Form.Item name="user_id" label="کارمند" rules={[{ required: true, message: 'کارمند را انتخاب کنید' }]}>
              <Select
                showSearch
                disabled={Boolean(editing)}
                optionFilterProp="label"
                placeholder="انتخاب کارمند"
                className="custom-font"
                popupClassName="custom-font"
                options={formMeta.employees.map((e) => ({
                  value: e.id,
                  label: `${e.name} — ${e.email}`,
                  disabled: e.is_operator && e.id !== editing?.user_id,
                }))}
              />
            </Form.Item>
            <Form.Item name="display_name" label="نام نمایشی برای مشتری (اختیاری)">
              <Input maxLength={150} placeholder="مثلاً: پشتیبانی - سارا" />
            </Form.Item>
            <Form.Item name="all_sources" valuePropName="checked" className="mb-2">
              <Checkbox>دسترسی به همه منابع (منابعی که بعداً اضافه شوند هم شامل می‌شود)</Checkbox>
            </Form.Item>
            {!allSources && (
              <Form.Item
                name="source_ids"
                label="منابع مجاز"
                rules={[{ required: true, type: 'array', min: 1, message: 'حداقل یک منبع انتخاب کنید' }]}
              >
                <Checkbox.Group className="flex flex-wrap gap-3">
                  {formMeta.sources.map((s) => (
                    <Checkbox key={s.id} value={s.id}>
                      <SourceTag source={s} />
                    </Checkbox>
                  ))}
                </Checkbox.Group>
              </Form.Item>
            )}
            {formMeta.departments.length > 0 && (
              <Form.Item name="department_ids" label="بخش‌های پاسخ‌گویی" extra="خالی = پاسخ‌گوی همه بخش‌ها">
                <Select
                  mode="multiple"
                  allowClear
                  className="custom-font"
                  options={formMeta.departments.map((d) => ({ value: d.id, label: d.name }))}
                />
              </Form.Item>
            )}
            <Form.Item
              name="max_concurrent_chats"
              label="سقف گفتگوی هم‌زمان برای ارجاع خودکار"
              extra="خالی = مقدار پیش‌فرض تنظیمات ماژول"
            >
              <InputNumber min={1} max={100} className="w-full" />
            </Form.Item>
            <Form.Item name="is_active" label="فعال" valuePropName="checked">
              <Switch checkedChildren="فعال" unCheckedChildren="غیرفعال" />
            </Form.Item>
            {!editing && (
              <Alert
                type="info"
                showIcon
                className="custom-font"
                title="اپراتور فقط گفتگوهای منابع انتخاب‌شده را در صندوق خود می‌بیند."
              />
            )}
          </Form>
        </Spin>
      </Modal>
    </WorkListPage>
  );
};

export default LiveChatOperators;
